import {Alert, DataStatus, EmptyStatus, Flag} from 'platform/components';
import {Box, HStack, Icon, Image, Show, VStack} from 'platform/foundation';
import {Lightbox, useLightbox} from 'platform/lightbox';
import {useFormatNumber} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {useState, type KeyboardEvent} from 'react';

import {isNil, isNotNil} from 'ramda';

import type {GetAdvertisementPreviewPhotosApiResponse} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SkeletonLoadingBox, usePermissions} from '@dms/shared';

import type {RequiredTestIdProps} from 'shared/web';

import {MAX_VISIBLE_THUMBNAILS} from '../constants/MAX_VISIBLE_THUMBNAILS';
import {PREVIEW_CONTAINER_BREAKPOINTS} from '../constants/PREVIEW_CONTAINER_BREAKPOINTS';
import {getPreviewContainerBreakpoint} from '../utils/getPreviewContainerBreakpoint';

const THUMBNAILS_BEFORE_OVERFLOW = MAX_VISIBLE_THUMBNAILS - 1;

export type AdvertisementPreviewPhotosQuery = {
  data?: GetAdvertisementPreviewPhotosApiResponse;
  isLoading: boolean;
  isError: boolean;
};

export function AdvertisementPreviewGallery(
  props: RequiredTestIdProps & {
    photosQuery: AdvertisementPreviewPhotosQuery;
    onAddPhotos: () => void;
    vehicleId: string;
  }
) {
  const formatNumber = useFormatNumber();
  const [canReadSettings] = usePermissions({permissionKeys: ['readSettings']});
  const photos = props.photosQuery.data?.photos ?? [];
  const isPhotosLoading = props.photosQuery.isLoading;
  const hasMissingWatermark =
    !isPhotosLoading &&
    !props.photosQuery.isError &&
    (props.photosQuery.data?.issues.some((issue) => issue.code === 'watermark_file_missing') ??
      false);
  const [lightboxControls, {onOpen}] = useLightbox(`advertisement-preview-${props.vehicleId}`);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const activePhotoIndex = Math.min(selectedPhotoIndex, Math.max(photos.length - 1, 0));
  const activePhoto = photos[activePhotoIndex];
  const isEmpty = !isPhotosLoading && !props.photosQuery.isError && isNil(activePhoto);
  const thumbnailStart = Math.max(
    0,
    Math.min(
      activePhotoIndex - (THUMBNAILS_BEFORE_OVERFLOW - 1),
      photos.length - MAX_VISIBLE_THUMBNAILS
    )
  );
  const thumbnailEnd = Math.min(thumbnailStart + MAX_VISIBLE_THUMBNAILS, photos.length);
  const hasMoreThumbnails = thumbnailEnd < photos.length;
  const visibleThumbnailEnd = hasMoreThumbnails ? thumbnailEnd - 1 : thumbnailEnd;
  const photoPosition = (index: number) =>
    i18n.t('page.advertisement.preview.photoPosition', {
      number: formatNumber(index + 1),
      total: formatNumber(photos.length),
    });
  const handleGalleryKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      !(event.target instanceof Node) ||
      !event.currentTarget.contains(event.target) ||
      photos.length < 2
    ) {
      return;
    }
    const direction = match(event.key)
      .with('ArrowLeft', () => -1)
      .with('ArrowRight', () => 1)
      .otherwise(() => null);
    if (isNil(direction)) {
      return;
    }
    event.preventDefault();
    setSelectedPhotoIndex((activePhotoIndex + direction + photos.length) % photos.length);
  };
  return (
    <PhotosColumn
      tabIndex={0}
      role="region"
      aria-label={i18n.t('entity.photo.labels.photos')}
      onKeyDown={handleGalleryKeyDown}
    >
      <Show when={hasMissingWatermark}>
        <Alert
          type="inline"
          variant="error"
          title={i18n.t('page.advertisement.preview.watermarkFileMissingTitle')}
          message={i18n.t('page.advertisement.preview.watermarkFileMissing')}
          hyperlinks={
            canReadSettings
              ? [
                  {
                    title: i18n.t('general.labels.settings'),
                    href: settingsRoutes.advertisingWatermarking,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  },
                ]
              : undefined
          }
          data-testid={`${props['data-testid']}-watermark-error`}
        />
      </Show>
      <VStack spacing={2} align="stretch">
        <PhotoFrame $isEmpty={isEmpty} data-testid={`${props['data-testid']}-photo-frame`}>
          <Show when={isPhotosLoading}>
            <PhotoLoadingFill>
              <SkeletonLoadingBox isLoading borderRadius="small" />
            </PhotoLoadingFill>
          </Show>
          <Show when={!isPhotosLoading}>
            <EmptyPhotosState>
              <DataStatus
                isError={props.photosQuery.isError}
                data-testid={`${props['data-testid']}-photo-status`}
              >
                {isNotNil(activePhoto) ? (
                  <>
                    <PhotoOpenButton
                      type="button"
                      aria-label={i18n.t('general.actions.viewDetail')}
                      onClick={() => onOpen(activePhotoIndex)}
                    >
                      <Image
                        src={activePhoto.url}
                        alt=""
                        width="100%"
                        height="100%"
                        fit="contain"
                        hasSpinner
                        data-testid={`${props['data-testid']}-main-photo`}
                      />
                    </PhotoOpenButton>
                    <Box position="absolute" top={2} left={2} pointerEvents="none">
                      <HStack spacing={1}>
                        <Show when={activePhoto.isCover}>
                          <Flag
                            label={i18n.t('entity.photo.labels.coverPhoto')}
                            colorScheme="blue"
                            isSubtle
                            size="small"
                          />
                        </Show>
                        <Show when={activePhoto.is360}>
                          <Flag
                            label={i18n.t('entity.vehicle.labels.threeHundredAndSixtyPhotos')}
                            colorScheme="neutral"
                            isSubtle
                            size="small"
                          />
                        </Show>
                        <Show when={activePhoto.isPromo}>
                          <Flag
                            label={i18n.t('page.advertisement.preview.promoPhoto')}
                            colorScheme="purple"
                            isSubtle
                            size="small"
                          />
                        </Show>
                      </HStack>
                    </Box>
                    <Box position="absolute" bottom={2} right={2} pointerEvents="none">
                      <span aria-live="polite">
                        <Flag
                          label={`${formatNumber(activePhotoIndex + 1)} / ${formatNumber(photos.length)}`}
                          colorScheme="neutral"
                          size="small"
                        />
                      </span>
                    </Box>
                    <Show when={photos.length > 1}>
                      <PhotoControls>
                        <PhotoNavigationButton
                          type="button"
                          aria-label={i18n.t('page.advertisement.preview.previousPhoto')}
                          onClick={() =>
                            setSelectedPhotoIndex(
                              (activePhotoIndex - 1 + photos.length) % photos.length
                            )
                          }
                        >
                          <Icon value="navigation/chevron_left" size={5} />
                        </PhotoNavigationButton>
                        <PhotoNavigationButton
                          type="button"
                          aria-label={i18n.t('page.advertisement.preview.nextPhoto')}
                          onClick={() =>
                            setSelectedPhotoIndex((activePhotoIndex + 1) % photos.length)
                          }
                        >
                          <Icon value="navigation/chevron_right" size={5} />
                        </PhotoNavigationButton>
                      </PhotoControls>
                    </Show>
                  </>
                ) : (
                  <EmptyStatus
                    variant="image"
                    headline={i18n.t('general.labels.noImages')}
                    action={{
                      title: i18n.t('general.actions.addPhotos'),
                      onClick: props.onAddPhotos,
                      leftIcon: 'image/add_photo_alternate',
                    }}
                  />
                )}
              </DataStatus>
            </EmptyPhotosState>
          </Show>
        </PhotoFrame>
        <Show when={isPhotosLoading || (!props.photosQuery.isError && photos.length > 1)}>
          <ThumbnailRow data-testid={`${props['data-testid']}-thumbnail-row`}>
            <Show when={isPhotosLoading}>
              {Array.from({length: MAX_VISIBLE_THUMBNAILS}, (_, index) => (
                <PhotoFrame key={index}>
                  <PhotoLoadingFill>
                    <SkeletonLoadingBox isLoading borderRadius="small" />
                  </PhotoLoadingFill>
                </PhotoFrame>
              ))}
            </Show>
            <Show when={!isPhotosLoading}>
              {photos.slice(thumbnailStart, visibleThumbnailEnd).map((photo, offset) => {
                const index = thumbnailStart + offset;
                return (
                  <ThumbnailButton
                    key={photo.fileId}
                    type="button"
                    aria-label={photoPosition(index)}
                    aria-pressed={activePhotoIndex === index}
                    $selected={activePhotoIndex === index}
                    onClick={() => setSelectedPhotoIndex(index)}
                  >
                    <Image src={photo.url} alt="" ratio="4 / 3" fit="cover" />
                  </ThumbnailButton>
                );
              })}
              <Show when={hasMoreThumbnails}>
                <ThumbnailButton
                  type="button"
                  aria-label={photoPosition(visibleThumbnailEnd)}
                  aria-pressed={activePhotoIndex === visibleThumbnailEnd}
                  $selected={activePhotoIndex === visibleThumbnailEnd}
                  onClick={() => setSelectedPhotoIndex(visibleThumbnailEnd)}
                >
                  <Image
                    src={photos[visibleThumbnailEnd]?.url ?? null}
                    alt=""
                    ratio="4 / 3"
                    fit="cover"
                  />
                  <ThumbnailOverflow>+{photos.length - visibleThumbnailEnd}</ThumbnailOverflow>
                </ThumbnailButton>
              </Show>
            </Show>
          </ThumbnailRow>
        </Show>
      </VStack>
      <Lightbox
        controls={lightboxControls}
        images={photos.map((photo) => ({id: photo.fileId, url: photo.url}))}
        data-testid={`${props['data-testid']}-lightbox`}
      />
    </PhotosColumn>
  );
}
const PhotoLoadingFill = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
`;

const PhotoFrame = styled.div<{$isEmpty?: boolean}>`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: ${({theme}) => theme.radii.small};
  background: ${({theme, $isEmpty}) =>
    $isEmpty ? 'transparent' : theme.colors.palettes.neutral[900][100]};
`;

const PhotoOpenButton = styled.button`
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  user-select: none;
  &:focus-visible {
    outline: ${({theme}) => `${theme.outline.default} ${theme.colors.general.accent}`};
    outline-offset: ${({theme}) => theme.outline.offset};
  }
`;

const PhotoControls = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
  padding: ${({theme}) => theme.getSize(2)};
`;

const PhotoNavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.getSize(9)};
  height: ${({theme}) => theme.getSize(9)};
  border: 0;
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  &:focus-visible {
    outline: ${({theme}) => `${theme.outline.default} ${theme.colors.general.accent}`};
    outline-offset: ${({theme}) => theme.outline.offset};
  }
`;

const ThumbnailButton = styled.button<{$selected: boolean}>`
  position: relative;
  padding: 0;
  overflow: hidden;
  border: ${({theme}) => theme.getSize(1)} solid
    ${({theme, $selected}) => ($selected ? theme.colors.severity.informational : 'transparent')};
  border-radius: ${({theme}) => theme.radii.small};
  background: transparent;
  cursor: pointer;
  user-select: none;
  &:focus-visible {
    outline: ${({theme}) => `${theme.outline.default} ${theme.colors.general.accent}`};
    outline-offset: ${({theme}) => theme.outline.offset};
  }
`;

const ThumbnailOverflow = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({theme}) => theme.colors.text.white};
  background: ${({theme}) => theme.colors.palettes.black[900][60]};
`;

const PhotosColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.getSize(3)};
  &:focus-visible {
    outline: ${({theme}) => `${theme.outline.default} ${theme.colors.general.accent}`};
    outline-offset: ${({theme}) => theme.outline.offset};
  }
`;

const EmptyPhotosState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ThumbnailRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${MAX_VISIBLE_THUMBNAILS}, minmax(0, 1fr));
  gap: ${({theme}) => theme.getSize(2)};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.compact
  )}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;
